import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from 'react';
import { useMotionValue, type MotionValue } from 'framer-motion';

export const CHATTEASER3_SCROLL_MODE = {
  Container: 'container',
  Wheel: 'wheel',
} as const;

const SCROLL_DELTA_IDLE = 0;

type ChatTeaser3ScrollMode =
  (typeof CHATTEASER3_SCROLL_MODE)[keyof typeof CHATTEASER3_SCROLL_MODE];

interface ChatTeaser3Scroll {
  resetScroll: () => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollY: MotionValue<number>;
}

export function useChatteaser3Scroll({
  maxScroll,
  mode,
  wrapperRef,
}: {
  maxScroll: number;
  mode: ChatTeaser3ScrollMode;
  wrapperRef: RefObject<HTMLDivElement | null>;
}): ChatTeaser3Scroll {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollY = useMotionValue(0);
  const maxScrollRef = useRef(maxScroll);

  useEffect(() => {
    maxScrollRef.current = maxScroll;
  }, [maxScroll]);

  useLayoutEffect(() => {
    if (mode !== CHATTEASER3_SCROLL_MODE.Container) return;

    const element = scrollContainerRef.current;
    if (!element) return;

    const onScroll = (): void => {
      scrollY.set(element.scrollTop);
    };

    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [maxScroll, mode, scrollY]);

  useEffect(() => {
    if (mode !== CHATTEASER3_SCROLL_MODE.Wheel) return;

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onWheel = (event: WheelEvent): void => {
      const currentScroll = scrollY.get();
      const maximumScroll = maxScrollRef.current;
      const canScrollDown =
        event.deltaY > SCROLL_DELTA_IDLE && currentScroll < maximumScroll;
      const canScrollUp =
        event.deltaY < SCROLL_DELTA_IDLE && currentScroll > SCROLL_DELTA_IDLE;
      if (!canScrollDown && !canScrollUp) return;

      event.preventDefault();
      const next = Math.min(
        maximumScroll,
        Math.max(SCROLL_DELTA_IDLE, currentScroll + event.deltaY),
      );
      scrollY.set(next);
    };

    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, [mode, scrollY, wrapperRef]);

  const resetScroll = useCallback((): void => {
    scrollY.set(0);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollY]);

  return {
    resetScroll,
    scrollContainerRef,
    scrollY,
  };
}
